// ============================================
// DAILY REPORTS PAGE
// Historique des rapports journaliers
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
    Calendar, DollarSign, ShoppingCart, Clock, 
    AlertTriangle, CheckCircle, ChevronRight, 
    RefreshCw, FileText, TrendingUp, CreditCard,
    Smartphone, Banknote, ChevronDown, ChevronUp,
    Shield, Eye, Mail, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const DailyReportsPage = () => {
    const { api, user } = useAuth();
    const [reports, setReports] = useState([]);
    const [currentCycle, setCurrentCycle] = useState(null);
    const [pendingAnomalies, setPendingAnomalies] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedReport, setExpandedReport] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(null);
    const [emailConfig, setEmailConfig] = useState(null);

    // Fetch all data
    const fetchData = useCallback(async () => {
        try {
            const [reportsRes, cycleRes, anomaliesRes] = await Promise.all([
                api().get('/daily-reports?limit=30'),
                api().get('/daily-reports/current-cycle'),
                api().get('/daily-reports/anomalies/pending')
            ]);
            
            setReports(reportsRes.data.reports || []);
            setCurrentCycle(cycleRes.data);
            setPendingAnomalies(anomaliesRes.data);
            
            // Fetch email config for Admin
            if (user?.role === 'ADMIN') {
                try {
                    const emailRes = await api().get('/daily-reports/config/email');
                    setEmailConfig(emailRes.data);
                } catch (e) {
                    // Email config not critical
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api, user]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Load report detail
    const loadReportDetail = async (date) => {
        if (selectedReport?.date === date) {
            setSelectedReport(null);
            return;
        }
        
        try {
            const response = await api().get(`/daily-reports/${date}`);
            setSelectedReport(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement du rapport');
        }
    };

    // Send report by email (Admin only)
    const sendReportEmail = async (reportDate) => {
        if (user?.role !== 'ADMIN') {
            toast.error('Seul l\'Admin peut envoyer des rapports par email');
            return;
        }
        
        setSendingEmail(reportDate);
        try {
            const response = await api().post('/daily-reports/send-email', {
                report_date: reportDate,
                include_detailed: true
            });
            
            if (response.data.status === 'success') {
                toast.success(`Rapport envoyé à ${response.data.recipient}`);
            } else if (response.data.status === 'skipped') {
                toast.warning(response.data.reason || 'Email non envoyé');
            } else {
                toast.error(response.data.message || 'Erreur d\'envoi');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSendingEmail(null);
        }
    };

    // Check if user can access
    const allowedRoles = ["ADMIN", "DIRECTEUR", "DIRECTEUR_ADJOINT"];
    if (!allowedRoles.includes(user?.role)) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                    <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
                    <h1 className="text-2xl font-bold mb-2">Accès refusé</h1>
                    <p className="text-gray-400">Vous n'avez pas accès à cette page</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-indigo-500/20">
                            <FileText className="w-8 h-8 text-indigo-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Rapports Journaliers</h1>
                            <p className="text-gray-400">Historique et archivage automatique</p>
                        </div>
                    </div>
                    <Button onClick={fetchData} variant="outline" size="sm">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                </div>

                {/* Current Cycle Card */}
                {currentCycle && (
                    <div className="p-6 rounded-2xl border bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border-blue-500/30">
                        <div className="flex items-center gap-3 mb-4">
                            <Calendar className="w-6 h-6 text-blue-400" />
                            <h2 className="text-xl font-semibold">Cycle en cours</h2>
                            <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                                En direct
                            </span>
                        </div>
                        
                        <div className="grid md:grid-cols-4 gap-4">
                            <div className="p-4 rounded-xl bg-gray-800/50">
                                <p className="text-sm text-gray-400 mb-1">Date</p>
                                <p className="text-lg font-bold">{currentCycle.formatted_date}</p>
                            </div>
                            <div className="p-4 rounded-xl bg-gray-800/50">
                                <p className="text-sm text-gray-400 mb-1">Chiffre d'affaires</p>
                                <p className="text-lg font-bold text-emerald-400">
                                    {currentCycle.total_revenue?.toLocaleString('fr-FR')} F
                                </p>
                            </div>
                            <div className="p-4 rounded-xl bg-gray-800/50">
                                <p className="text-sm text-gray-400 mb-1">Commandes</p>
                                <p className="text-lg font-bold">{currentCycle.total_orders}</p>
                            </div>
                            <div className="p-4 rounded-xl bg-gray-800/50">
                                <p className="text-sm text-gray-400 mb-1">Heure actuelle</p>
                                <p className="text-lg font-bold font-mono">{currentCycle.current_time}</p>
                            </div>
                        </div>

                        {/* Payment breakdown */}
                        <div className="grid grid-cols-3 gap-3 mt-4">
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-800/30">
                                <Banknote className="w-5 h-5 text-green-400" />
                                <div>
                                    <p className="text-xs text-gray-400">Espèces</p>
                                    <p className="font-semibold">{currentCycle.breakdown_by_payment?.cash?.toLocaleString('fr-FR') || 0} F</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-800/30">
                                <Smartphone className="w-5 h-5 text-blue-400" />
                                <div>
                                    <p className="text-xs text-gray-400">Mobile</p>
                                    <p className="font-semibold">{currentCycle.breakdown_by_payment?.mobile?.toLocaleString('fr-FR') || 0} F</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-800/30">
                                <CreditCard className="w-5 h-5 text-purple-400" />
                                <div>
                                    <p className="text-xs text-gray-400">Carte</p>
                                    <p className="font-semibold">{currentCycle.breakdown_by_payment?.card?.toLocaleString('fr-FR') || 0} F</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pending Anomalies Alert */}
                {pendingAnomalies?.total_pending > 0 && (
                    <div className="p-6 rounded-2xl border bg-red-500/10 border-red-500/30">
                        <div className="flex items-center gap-3 mb-3">
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                            <h2 className="text-xl font-semibold">Anomalies à traiter</h2>
                            <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-medium">
                                {pendingAnomalies.total_pending}
                            </span>
                        </div>
                        <p className="text-gray-400 mb-4">
                            Des caisses ont été fermées automatiquement à minuit et nécessitent une vérification
                        </p>
                        <div className="space-y-2">
                            {pendingAnomalies.midnight_closures?.slice(0, 3).map((closure, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50">
                                    <div className="flex items-center gap-3">
                                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                                        <div>
                                            <p className="font-medium">{closure.register_name}</p>
                                            <p className="text-sm text-gray-400">{closure.user_name} • {closure.date}</p>
                                        </div>
                                    </div>
                                    <span className="text-amber-400 text-sm font-medium">À vérifier</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Reports History */}
                <div className="p-6 rounded-2xl border bg-gray-800/60 border-gray-700">
                    <div className="flex items-center gap-3 mb-6">
                        <Clock className="w-6 h-6 text-gray-400" />
                        <h2 className="text-xl font-semibold">Historique des rapports</h2>
                    </div>

                    {reports.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Aucun rapport archivé</p>
                            <p className="text-sm">Les rapports sont générés automatiquement à 00h05</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {reports.map((report) => (
                                <div key={report.id} className="border border-gray-700 rounded-xl overflow-hidden">
                                    {/* Report Header */}
                                    <button
                                        onClick={() => loadReportDetail(report.date)}
                                        className="w-full p-4 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`p-2 rounded-lg ${
                                                report.anomalies_count > 0 
                                                    ? 'bg-amber-500/20' 
                                                    : 'bg-emerald-500/20'
                                            }`}>
                                                {report.anomalies_count > 0 ? (
                                                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                                                ) : (
                                                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                                                )}
                                            </div>
                                            <div className="text-left">
                                                <p className="font-semibold">{report.date}</p>
                                                <p className="text-sm text-gray-400">
                                                    {report.registers_count} caisse(s)
                                                    {report.anomalies_count > 0 && (
                                                        <span className="text-amber-400 ml-2">
                                                            • {report.anomalies_count} anomalie(s)
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-6">
                                            <div className="text-right">
                                                <p className="font-bold text-emerald-400">
                                                    {report.total_revenue?.toLocaleString('fr-FR')} F
                                                </p>
                                                <p className="text-sm text-gray-400">
                                                    {report.total_orders} commandes
                                                </p>
                                            </div>
                                            {selectedReport?.date === report.date ? (
                                                <ChevronUp className="w-5 h-5 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            )}
                                        </div>
                                    </button>

                                    {/* Report Details (expanded) */}
                                    {selectedReport?.date === report.date && (
                                        <div className="p-4 border-t border-gray-700 bg-gray-800/30">
                                            <div className="grid md:grid-cols-3 gap-4 mb-4">
                                                {/* Payment breakdown */}
                                                <div className="space-y-2">
                                                    <p className="text-sm font-medium text-gray-400">Par paiement</p>
                                                    <div className="space-y-1">
                                                        <div className="flex justify-between text-sm">
                                                            <span className="flex items-center gap-2">
                                                                <Banknote className="w-4 h-4 text-green-400" />
                                                                Espèces
                                                            </span>
                                                            <span>{selectedReport.breakdown_by_payment?.cash?.toLocaleString('fr-FR') || 0} F</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span className="flex items-center gap-2">
                                                                <Smartphone className="w-4 h-4 text-blue-400" />
                                                                Mobile
                                                            </span>
                                                            <span>{selectedReport.breakdown_by_payment?.mobile?.toLocaleString('fr-FR') || 0} F</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span className="flex items-center gap-2">
                                                                <CreditCard className="w-4 h-4 text-purple-400" />
                                                                Carte
                                                            </span>
                                                            <span>{selectedReport.breakdown_by_payment?.card?.toLocaleString('fr-FR') || 0} F</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Zone breakdown */}
                                                <div className="space-y-2">
                                                    <p className="text-sm font-medium text-gray-400">Par zone</p>
                                                    <div className="space-y-1">
                                                        {Object.entries(selectedReport.breakdown_by_zone || {}).map(([zone, amount]) => (
                                                            <div key={zone} className="flex justify-between text-sm">
                                                                <span>{zone}</span>
                                                                <span>{amount?.toLocaleString('fr-FR')} F</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Registers */}
                                                <div className="space-y-2">
                                                    <p className="text-sm font-medium text-gray-400">Caisses</p>
                                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                                        {(selectedReport.registers || []).map((reg, idx) => (
                                                            <div key={idx} className="flex justify-between text-sm">
                                                                <span className="truncate">{reg.register_name}</span>
                                                                <span className={
                                                                    reg.closure_status === 'midnight_auto_closed' 
                                                                        ? 'text-amber-400' 
                                                                        : 'text-gray-300'
                                                                }>
                                                                    {reg.total_sales?.toLocaleString('fr-FR')} F
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Anomalies */}
                                            {selectedReport.anomalies?.length > 0 && (
                                                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                                                    <p className="text-sm font-medium text-amber-400 mb-2">
                                                        ⚠️ Anomalies détectées
                                                    </p>
                                                    <div className="space-y-1">
                                                        {selectedReport.anomalies.map((anomaly, idx) => (
                                                            <p key={idx} className="text-sm text-gray-300">
                                                                • {anomaly.message}
                                                            </p>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Metadata */}
                                            <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between text-sm text-gray-400">
                                                <span>Archivé le {selectedReport.local_time}</span>
                                                <div className="flex items-center gap-3">
                                                    {/* Send Email Button (Admin only) */}
                                                    {user?.role === 'ADMIN' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                sendReportEmail(selectedReport.date);
                                                            }}
                                                            disabled={sendingEmail === selectedReport.date}
                                                            className="text-blue-400 border-blue-400/50 hover:bg-blue-400/10"
                                                        >
                                                            {sendingEmail === selectedReport.date ? (
                                                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                            ) : (
                                                                <Mail className="w-4 h-4 mr-1" />
                                                            )}
                                                            Envoyer par email
                                                        </Button>
                                                    )}
                                                    <span className="flex items-center gap-2">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Rapport verrouillé
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Email configuration info (Admin only) */}
                {user?.role === 'ADMIN' && emailConfig && (
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 text-sm">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-blue-300 mb-1">Envoi automatique des rapports par email</p>
                                <p className="text-gray-400">
                                    Les rapports PDF sont envoyés automatiquement à <strong className="text-white">{emailConfig.admin_email}</strong> après chaque reset de minuit.
                                    {!emailConfig.resend_configured && (
                                        <span className="text-amber-400 block mt-1">
                                            ⚠️ Clé API Resend non configurée - les emails ne seront pas envoyés.
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info about automatic reset */}
                <div className="p-4 rounded-xl bg-gray-800/40 border border-gray-700 text-sm text-gray-400">
                    <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-gray-300 mb-1">Archivage automatique à 00h05</p>
                            <p>
                                Chaque jour à 00h05, le système archive automatiquement les données de la veille 
                                et démarre un nouveau cycle. Les caisses encore ouvertes sont fermées automatiquement 
                                et marquées comme anomalies.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailyReportsPage;
